import React, { FC, useContext, useMemo } from 'react';
import { Box } from '@mui/material';
import { EvIoGameSetupSettings, GameEntity, PlatformType } from '@okletsplay/core';
import { MatchAccessibility } from '@okletsplay/core/src/modules/match/enums';
import { MatchEntity, MatchPlayer } from 'app/interfaces/entity';
import appMatchSummaryChallenge from 'assets/images/app_match_summary-challenge-match.jpeg';
import appMatchSummaryPrivateMatch from 'assets/images/app_match_summary-private-match.jpg';
import appMatchSummaryPublicMatch from 'assets/images/app_match_summary-public-match.jpg';
import appMatchSummaryWinnings from 'assets/images/app_match_summary-winnings.jpg';
import { MultiCurrencyConfig } from 'config/multi-currency-config';
import { ChessMatchTypeFormat } from 'shared/components/Format/ChessMatchTypeFormat';
import { GameCurrency, GameCurrencyWithFee } from 'modules/game-payments/components';
import { EvIoMatchTypeFormat } from 'shared/components/Format/EvIoMatchTypeFormat';
import { calculateMatchWinnings } from 'modules/game-payments/utils';
import { MatchSummaryCard } from './MatchSummaryCard';
import { GameActivityPaymentContext } from 'modules/game-payments/providers';

interface MatchSummaryProps {
  players?: MatchPlayer[];
  maxPlayersCount: number;
  accessibility?: MatchAccessibility;
  entryFee: number;
  clockLimit?: number;
  clockIncrement?: number;
  game?: Partial<GameEntity>;
  matchSetupSettings?: MatchEntity['matchSetupSettings'];
}

export const MatchSummary: FC<MatchSummaryProps> = ({
  accessibility,
  game,
  players,
  maxPlayersCount,
  entryFee,
  clockLimit = 0,
  clockIncrement = 0,
  matchSetupSettings,
}) => {
  const { platformFeePercentage } = useContext(GameActivityPaymentContext);

  const challengeDescription = useMemo(
    () => players?.map((player) => player?.user?.userName).join(' vs. '),
    [players]
  );

  const matchNote = useMemo(() => {
    const expirationTime = matchSetupSettings?.maxPendingDuration ?? 0;
    if (accessibility !== MatchAccessibility.Public || !players) {
      return '';
    }

    const days = Math.floor(expirationTime / 60 / 60 / 24);

    if (days > 1) {
      return `Note: open matches expire after ${days} days of inactivity.`;
    }

    const hours = Math.floor(expirationTime / 60 / 60);
    if (days < 1 && hours >= 1) {
      return `Note: open matches expire after ${hours} hours of inactivity.`;
    }

    const minutes = Math.floor(expirationTime / 60);
    if (days < 1 && hours < 1 && minutes >= 1) {
      return `Note: open matches expire after ${minutes} minutes of inactivity.`;
    }
  }, [accessibility, matchSetupSettings?.maxPendingDuration, players]);

  const winnings = useMemo(() => {
    if (MultiCurrencyConfig.isEnabledForMatches()) {
      return calculateMatchWinnings(entryFee, 0, 1, maxPlayersCount);
    }

    return calculateMatchWinnings(entryFee, platformFeePercentage);
  }, [entryFee, platformFeePercentage, maxPlayersCount]);

  const gameDescription = useMemo(() => {
    const platformType = game?.platformType;
    const defaultLabel = 'Standard • 1v1';

    if (!platformType) {
      return defaultLabel;
    }

    if ([PlatformType.Lichess, PlatformType.Lidraughts].includes(platformType)) {
      return (
        <>
          Standard • 1v1 • {<ChessMatchTypeFormat limit={clockLimit} increment={clockIncrement} />}
        </>
      );
    }

    if (platformType === PlatformType.EvIo && matchSetupSettings) {
      return (
        <EvIoMatchTypeFormat
          settings={matchSetupSettings as unknown as EvIoGameSetupSettings}
          gameId={game?.id ?? ''}
        />
      );
    }

    return defaultLabel;
  }, [
    clockIncrement,
    clockLimit,
    game?.id,
    game?.platformType,
    matchSetupSettings,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(matchSetupSettings), // matchSetupSettings reference is not changed, but need to look at the object contents.
  ]);

  const matchDetailsOptions = {
    [MatchAccessibility.Private]: (
      <MatchSummaryCard
        image={appMatchSummaryPrivateMatch}
        title="Private Match"
        description="Each team and player is hand-picked"
      />
    ),
    [MatchAccessibility.Public]: (
      <MatchSummaryCard
        note={matchNote}
        title="Open Match"
        image={appMatchSummaryPublicMatch}
        description="Public match that anybody can request to join."
      />
    ),
  };

  return (
    <Box>
      {players && accessibility === MatchAccessibility.Private ? (
        <MatchSummaryCard
          image={appMatchSummaryChallenge}
          title="Challenge Match"
          description={challengeDescription}
        />
      ) : (
        matchDetailsOptions[accessibility as MatchAccessibility]
      )}
      <MatchSummaryCard
        title={game?.title}
        image={game?.selectionImage}
        description={gameDescription}
      />
      <MatchSummaryCard
        image={appMatchSummaryWinnings}
        title={
          <>
            Entry Fee: <GameCurrencyWithFee amount={entryFee} />
          </>
        }
        description={
          <>
            Potential Winnings: <GameCurrency amount={winnings} />
          </>
        }
      />
    </Box>
  );
};
